import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/layouts/Layout";
import Section from "../../../components/base/Section";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { clearToolbar, isRoot, setPage, setToolbarActions, useHub } from "../../../core/Global";
import { toastError } from "../../../components/base/Toast";
import { Quote, QuoteStatus, statusClass, statusText } from "../models/Enterprise";
import { toDate, toMoney } from "../../plancatalog/components/format";
import QuoteBuilderDrawer from "../components/QuoteBuilderDrawer";
import QuoteDetailDrawer from "../components/QuoteDetailDrawer";
import EnterpriseSettingsDrawer from "../components/EnterpriseSettingsDrawer";
import "./EnterpriseView.css";


type Filter = "all" | "open" | "draft" | "active" | "closed"

const filters: [Filter, string][] = [
    ["all", "All"], ["open", "With the customer"], ["draft", "Drafts"], ["active", "Active"], ["closed", "Closed"]
]

const isOpen = (q: Quote) => q.status === "sent" || q.status === "accepted"
const isClosed = (q: Quote) => q.status === "expired" || q.status === "revoked" || q.status === "superseded"


// Enterprise offers (spec §1.6): every offer, where it stands with the customer, and the way to
// build, send and follow one. Rinvox's own page -- root only for now.
export default function EnterpriseView() {

    const hub = useHub()

    const [quotes, quotesSet] = useState<Quote[]>([])
    const [loading, loadingSet] = useState<boolean>(true)
    const [filter, filterSet] = useState<Filter>("all")
    const [search, searchSet] = useState<string>("")

    const [building, buildingSet] = useState<{ quote?: Quote }>()
    const [viewing, viewingSet] = useState<string>()
    const [settingsOpen, settingsOpenSet] = useState<boolean>(false)

    const load = (silent: boolean = false) => {
        if (!silent) loadingSet(true)
        hub.Get("/system/enterprise/", res => {
            loadingSet(false)
            if (res.success) quotesSet(res.data ?? [])
            else toastError(res.message ?? "Couldn't load the offers.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        setPage("Enterprise")
        if (isRoot()) load()
        return () => { clearToolbar() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // The page's buttons live in the header, beside its title -- not in a second header of the
    // page's own. Set again whenever the refresh spinner has to change.
    useEffect(() => {
        if (!isRoot()) return
        setToolbarActions(() => (
            <>
                <button type="button" className="btn btn-sm btn-light" onClick={() => settingsOpenSet(true)}>
                    <Icon icon="sliders" type="solid" size={12} className="me-2" />
                    Settings
                </button>
                <button type="button" className="btn btn-sm btn-light-primary" disabled={loading} onClick={() => load()}>
                    <Icon icon="arrows-rotate" type="solid" size={12} className={"me-2" + (loading ? " fa-spin" : "")} />
                    Refresh
                </button>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => buildingSet({})}>
                    <Icon icon="plus" type="solid" size={12} className="me-2" />
                    New offer
                </button>
            </>
        ))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading])

    const counts = useMemo(() => ({
        all: quotes.length,
        open: quotes.filter(isOpen).length,
        draft: quotes.filter(q => q.status === "draft").length,
        active: quotes.filter(q => q.status === "active").length,
        closed: quotes.filter(isClosed).length,
        monthly: quotes.filter(q => q.status === "active").reduce((n, q) => n + q.monthly, 0),
        changes: quotes.filter(q => isOpen(q)).reduce((n, q) => n + (q.changeRequests?.length ?? 0), 0)
    }), [quotes])

    const shown = useMemo(() => {
        const s = search.trim().toLowerCase()
        return quotes
            .filter(q => filter === "all" || (filter === "open" ? isOpen(q) : filter === "closed" ? isClosed(q) : q.status === filter))
            .filter(q => !s || [q.companyName, q.contactName, q.contactEmail, "q-" + q.number]
                .some(v => (v ?? "").toLowerCase().includes(s)))
    }, [quotes, filter, search])

    if (!isRoot()) {
        return (
            <Layout wide>
                <Section fluid className="py-20 text-center text-gray-600">
                    This page is for the root account only.
                </Section>
            </Layout>
        )
    }

    return (
        <Layout wide>
            <Section fluid className="py-6 ent-page">

                <div className="ent-stats">
                    <div className="ent-stat">
                        <div className="ent-stat-value">{counts.open}</div>
                        <div className="ent-stat-label">With the customer</div>
                    </div>
                    <div className="ent-stat is-warn">
                        <div className="ent-stat-value">{counts.changes}</div>
                        <div className="ent-stat-label">Change requests waiting</div>
                    </div>
                    <div className="ent-stat is-good">
                        <div className="ent-stat-value">{counts.active}</div>
                        <div className="ent-stat-label">Active customers</div>
                    </div>
                    <div className="ent-stat is-good">
                        <div className="ent-stat-value">{toMoney(counts.monthly)}</div>
                        <div className="ent-stat-label">Active, a month</div>
                    </div>
                </div>

                <div className="ent-toolbar">
                    <div className="d-flex flex-wrap gap-2">
                        {
                            filters.map(([key, label]) =>
                                <button key={key} type="button" className={"ent-filter" + (filter === key ? " is-active" : "")}
                                    onClick={() => filterSet(key)}>
                                    {label}<span>{counts[key]}</span>
                                </button>
                            )
                        }
                    </div>
                    <div className="ent-search">
                        <Icon icon="magnifying-glass" size={12} />
                        <input type="text" placeholder="Search company, contact, Q-number"
                            value={search} onChange={e => searchSet(e.target.value)} />
                    </div>
                </div>

                {
                    loading && quotes.length === 0 ?
                        <div className="py-20"><Indicator /></div>
                        : shown.length === 0 ?
                            <div className="ent-empty">
                                <Icon icon="building" type="thin" size={48} />
                                <div className="fw-bold text-gray-800 mt-4">{quotes.length === 0 ? "No offers yet" : "Nothing matches"}</div>
                                {
                                    quotes.length === 0 &&
                                    <div className="text-gray-500 fs-7 mt-1">
                                        Set the item prices, costs and documents under Settings first, then build your first offer.
                                    </div>
                                }
                            </div>
                            :
                            <div className="ent-list">
                                {shown.map(q => <QuoteRow key={q.id} quote={q} onOpen={() => viewingSet(q.id)} />)}
                            </div>
                }

            </Section>

            {
                building &&
                <QuoteBuilderDrawer
                    quote={building.quote}
                    onClose={() => buildingSet(undefined)}
                    onSaved={q => { load(true); viewingSet(q.id) }} />
            }

            {
                viewing && !building &&
                <QuoteDetailDrawer
                    id={viewing}
                    onClose={() => viewingSet(undefined)}
                    onChanged={() => load(true)}
                    onEdit={q => buildingSet({ quote: q })}
                    onOpenVersion={id => viewingSet(id)} />
            }

            {
                settingsOpen &&
                <EnterpriseSettingsDrawer
                    onClose={() => settingsOpenSet(false)} />
            }
        </Layout>
    )
}


function QuoteRow(props: { quote: Quote, onOpen: () => void }) {
    const q = props.quote
    const status = q.status as QuoteStatus
    const changes = q.changeRequests?.length ?? 0

    const when =
        q.status === "draft" ? `Created ${toDate(q.createdAt)}`
            : q.status === "sent" ? `Sent ${toDate(q.sentAt)} · open until ${toDate(q.validUntil)}`
                : q.status === "accepted" ? `Signed ${toDate(q.signature?.signedAt)} · waiting for payment`
                    : q.status === "active" ? `Active since ${toDate(q.activatedAt)}`
                        : q.status === "expired" ? `Expired ${toDate(q.validUntil)}`
                            : `Closed ${toDate(q.revokedAt ?? q.modifiedAt)}`

    return (
        <button type="button" className={"ent-row is-" + q.status} onClick={props.onOpen}>
            <div className="ent-row-ref">
                <div className="ent-row-number">Q-{q.number}{q.version > 1 && <span>v{q.version}</span>}</div>
                <span className={"badge " + statusClass[status]}>{statusText[status]}</span>
            </div>
            <div className="ent-row-who min-w-0">
                <div className="fw-bold text-gray-900 text-truncate">{q.companyName || "Untitled offer"}</div>
                <div className="text-gray-500 fs-8 text-truncate">
                    {q.contactName}{q.contactEmail && <> · {q.contactEmail}</>}
                    {q.existingUserId && <span className="text-primary"> · existing account</span>}
                </div>
            </div>
            <div className="ent-row-money">
                <div className="fw-bolder text-gray-900">{toMoney(q.monthly)}<small>/mo</small></div>
                <div className="text-gray-500 fs-8">
                    {q.includedUsers} users{q.oneTime > 0 && <> · {toMoney(q.oneTime)} one-time</>}
                </div>
            </div>
            <div className="ent-row-when">
                <div className="fs-8 text-gray-600">{when}</div>
                <div className="ent-row-flags">
                    {q.openCount > 0 && <span title="Times the customer opened the link"><Icon icon="eye" size={10} />{q.openCount}</span>}
                    {changes > 0 && <span className="is-warn" title="Change requests"><Icon icon="message-pen" size={10} />{changes}</span>}
                    {q.status === "draft" && q.blockers?.length > 0 && <span className="is-bad" title={q.blockers[0]}><Icon icon="ban" size={10} />{q.blockers.length}</span>}
                </div>
            </div>
            <Icon icon="chevron-right" size={12} className="text-gray-400" />
        </button>
    )
}
